import { useState } from 'react';
import styled from '@emotion/styled';
import { renderQuestions } from '../../../../components/helpers/renderFormQuestions';
import { Button } from 'semantic-ui-react';
import { screenSizes } from 'src/assets/styles/Literals';

const QuizContentWrapper = styled.div`
  .quiz-container {
    background-color: #f8f9fa;
    padding: 2rem;
    border-radius: 8px;
    margin-bottom: 2rem;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 1rem;
      margin-bottom: 1rem;
    }
  }

  .quiz-header {
    margin-bottom: 2rem;
    text-align: center;

    h3 {
      font-family: 'Raleway', sans-serif;
      color: #2c3e50;
      margin-bottom: 1rem;

      @media (max-width: ${screenSizes.tablet}) {
        font-size: 1.5rem;
      }

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 1.25rem;
      }
    }

    @media (max-width: ${screenSizes.tablet}) {
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin-bottom: 1rem;
    }
  }

  .quiz-progress {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background-color: white;
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    font-weight: 500;
    color: #2c3e50;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.6rem;
      font-size: 0.95rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.5rem;
      font-size: 0.9rem;
    }
  }

  .quiz-results {
    text-align: center;
    padding: 2rem;
    background-color: white;
    border-radius: 8px;
    margin-bottom: 2rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

    @media (max-width: ${screenSizes.tablet}) {
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 1rem;
      margin-bottom: 1rem;
    }
  }

  .result-message {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
    color: ${({ passed }) => (passed ? '#4CAF50' : '#f44336')};
    font-weight: 500;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 1.1rem;
      margin-bottom: 1.25rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }

  .navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e0e0e0;

    @media (max-width: ${screenSizes.tablet}) {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      flex-direction: column;
      gap: 1rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin-top: 1rem;
      padding-top: 1rem;
      gap: 0.75rem;
    }
  }

  .styled-button {
    && {
      @media (max-width: ${screenSizes.tablet}) {
        min-width: 120px !important;
        font-size: 0.95rem !important;
      }

      @media (max-width: ${screenSizes.mobile}) {
        min-width: 100px !important;
        font-size: 0.9rem !important;
        padding: 0.6rem 1rem !important;
      }
    }
  }
`;

const QuizContent = ({ quiz, onComplete, handleContinue }) => {
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    answers: {},
    showResults: false,
    passed: false,
  });

  const handleQuizInput = (event) => {
    const { name, value } = event.target;
    setQuizState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [name]: value,
      },
    }));
  };

  const handleQuizSubmit = () => {
    const { questions, passingScore = 70 } = quiz;
    const totalQuestions = questions.length;
    const correctAnswers = questions.reduce((acc, question, index) => {
      const userAnswer = quizState.answers[`question_${index}`];
      return acc + (userAnswer === question.correctAnswer ? 1 : 0);
    }, 0);

    const score = (correctAnswers / totalQuestions) * 100;
    const passed = score >= passingScore;

    setQuizState((prev) => ({
      ...prev,
      showResults: true,
      passed,
    }));

    if (passed) {
      onComplete();
    }
  };

  if (!quiz) return null;

  const { questions } = quiz;
  const currentQuestion = questions[quizState.currentQuestion];

  if (quizState.showResults) {
    return (
      <QuizContentWrapper>
        <div className="quiz-results">
          <div className="result-message" passed={quizState.passed}>
            {quizState.passed
              ? '¡Felicitaciones! Has aprobado el quiz.'
              : 'No has alcanzado la puntuación necesaria. Por favor, intenta nuevamente.'}
          </div>
          {!quizState.passed ? (
            <Button
              className="styled-button"
              onClick={() =>
                setQuizState({
                  currentQuestion: 0,
                  answers: {},
                  showResults: false,
                  passed: false,
                })
              }
            >
              Intentar Nuevamente
            </Button>
          ) : (
            <Button className="styled-button" primary onClick={handleContinue}>
              Continuar
            </Button>
          )}
        </div>
      </QuizContentWrapper>
    );
  }

  const quizQuestions = [
    {
      type: 'title',
      text: `Pregunta ${quizState.currentQuestion + 1} de ${questions.length}`,
    },
    {
      type: 'text',
      text: currentQuestion.question,
    },
    ...currentQuestion.options.map((option) => ({
      type: 'checkbox',
      id: `question_${quizState.currentQuestion}`,
      radio: true,
      options: [option],
    })),
  ];

  return (
    <QuizContentWrapper>
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>Evaluación</h3>
          <div className="quiz-progress">
            <span>
              Progreso: {quizState.currentQuestion + 1}/{questions.length}
            </span>
          </div>
        </div>
        {renderQuestions({
          questions: quizQuestions,
          inputs: quizState.answers,
          handleInputs: handleQuizInput,
        })}
        <div className="navigation">
          {quizState.currentQuestion > 0 ? (
            <Button
              className="styled-button"
              onClick={() =>
                setQuizState((prev) => ({
                  ...prev,
                  currentQuestion: prev.currentQuestion - 1,
                }))
              }
            >
              Anterior
            </Button>
          ) : (
            <div />
          )}
          {quizState.currentQuestion < questions.length - 1 ? (
            <Button
              className="styled-button"
              onClick={() =>
                setQuizState((prev) => ({
                  ...prev,
                  currentQuestion: prev.currentQuestion + 1,
                }))
              }
            >
              Siguiente
            </Button>
          ) : (
            <Button className="styled-button" secondary onClick={handleQuizSubmit}>
              Finalizar Quiz
            </Button>
          )}
        </div>
      </div>
    </QuizContentWrapper>
  );
};

export { QuizContent };
